import { getConfigWithMetadata } from '@pkgs/api/utils/headers';
import { getErrorResult } from '@pkgs/api/utils/error';
import { createSessionToken } from '@pkgs/api/utils';
import { type ApiResult, type GetLocationSuggestionOptions, type ApiConfig } from '@pkgs/api/types';
import { type AutoSuggestionPlaceResponse, commonSuggest, type CommonSuggestParams, PlaceType } from '@generated/search-bff';

/**
 * Fetch location suggestions based on the provided query and options using the `/api/v2/autosuggestion/suggest` endpoint.
 *
 * {@link https://search-docs-frontend-engelvoelkers.vercel.app/frontend/api/services/autosuggestion/getPlaceSuggestions Search API Documentation}
 */
export async function getPlaceSuggestions(
  query: string,
  options: GetLocationSuggestionOptions,
  apiConfig: ApiConfig,
): Promise<ApiResult<AutoSuggestionPlaceResponse[]>> {
  try {
    const _apiConfig = getConfigWithMetadata(apiConfig, 'getPlaceSuggestions');

    const suggestPayload: CommonSuggestParams = {
      ...options,
      sessionToken: options.sessionToken ?? createSessionToken(),
      input: query,
      limit: options.limit ?? 10,
      types: options.types ?? [
        PlaceType.country,
        PlaceType.neighborhood,
        PlaceType.locality,
        PlaceType.administrative_area_level_1,
        PlaceType.administrative_area_level_4,
        PlaceType.postal_code,
        PlaceType.sublocality,
      ],
      filterCustomPlaceTypes: options.filterCustomPlaceTypes ?? false,
    };

    const results = await commonSuggest(suggestPayload, _apiConfig);

    return {
      status: 'success',
      data: results,
    };
  } catch (error) {
    apiConfig.onError?.(error);
    return getErrorResult(error);
  }
}
